import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { merge } from 'ts-deepmerge';

import { useDashboardWidgetSettings } from '../../../../context/useDashboardWidgetSettings';
import { resolveEffectiveTarget } from '../../targetUtils';
import type { GigawidgetSettings } from '../../universal-widget/util';
import { useDashboardStore } from '../../useDashboardStore';
import type {
  DataSourceVariables,
  DateFilterOptions,
  WidgetDataSource,
} from '../types';

const applyFilter = <T, K>(filterFunc?: (value: T) => K, value?: T) => {
  if (Array.isArray(value) && value.length === 0) {
    return {};
  }
  if (value == null) {
    return {};
  }
  if (!filterFunc) {
    return {};
  }

  return filterFunc(value);
};

export const useDataSourceFilter = <T extends WidgetDataSource>(
  dataSource: T,
  dateFilterOptions?: DateFilterOptions<T>
) => {
  const { filters, myItemsFilters, myItemsTarget } = useDashboardStore();
  const [settings] = useDashboardWidgetSettings<GigawidgetSettings>();
  const { user } = useRisksmartUser();

  const departmentFilter = applyFilter(
    dataSource.dashboardFilterConfig.departmentsFilter,
    filters.departments
  );
  const tagFilter = applyFilter(
    dataSource.dashboardFilterConfig.tagsFilter,
    filters.tags
  );
  const dateFilter = applyFilter(
    (dateRange) =>
      dateFilterOptions?.dateFilter?.(
        dateRange,
        dateFilterOptions?.precision ?? 'day'
      ) ?? {},
    filters.dateRange
  );

  // For a user target the picker rescopes the dashboard to that user, so
  // every widget's ownership filter should see the target's id as the
  // "current user". Group targets keep userId as the logged-in user — the
  // raw target is forwarded as a separate arg so per-data-source filters
  // (those that route through `getQueryVariables`) can express group
  // semantics. The redundancy for user targets (id passed twice — once as
  // `effectiveUserId`, once inside `target`) is intentional: it keeps
  // ownership filters that don't use `getQueryVariables` (e.g. attestations)
  // working for user targets without each having to handle target itself.
  const { effectiveUserId } = resolveEffectiveTarget(
    myItemsTarget,
    user?.userId
  );

  const ownershipFilter = applyFilter(
    (ownershipFilters) =>
      dataSource.dashboardFilterConfig.ownershipFilter?.(
        ownershipFilters,
        effectiveUserId,
        myItemsTarget
      ) ?? {},
    myItemsFilters
  );

  if (settings?.allowOwnershipFiltering) {
    return ownershipFilter as DataSourceVariables<T>;
  }

  return merge(
    departmentFilter,
    tagFilter,
    settings?.ignoreDashboardDateFilter ? {} : dateFilter
  ) as DataSourceVariables<T>;
};
